import React, { useEffect, useState } from 'react';
import { Box, Dialog, DialogContent, DialogTitle, FormControlLabel, IconButton, Radio, RadioGroup, Typography, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { putUserIdNotification } from '../../../apis/settings/settingsAPI';
import { useTranslation } from 'react-i18next';

interface EmployeePopupProps {
  isOpen: boolean;
  handleClose: () => void;
  employeeData: {
    id: number;
    name: string;
    username: string;
    email: string;
    notificationType?: string;
    groupName?: string;
  } | null; // userData가 null일 수 있으므로 null 허용
}

export default function EmployeePopup({ isOpen, handleClose, employeeData }: EmployeePopupProps) {
  const [notificationType, setNotificationType] = useState(employeeData?.notificationType); // 알람 상태
  // 다국어
  const [language, setLanguage] = useState(localStorage.getItem('language') || 'ko');
  const { t, i18n } = useTranslation(); // useTranslation 훅 사용
  // 다크 라이트 모드
  const theme = useTheme();

  // 알림방법 변경 상태 업데이트
  const handleNotificationTypeChange = async (event: any) => {
    const prevNotificationType = notificationType;
    const newNotificationType = event.target.value;
    if (notificationType == newNotificationType) {
      return;
    }
    setNotificationType(newNotificationType);

    const data = { notificationType: newNotificationType };

    try {
      const response = await putUserIdNotification(data, employeeData?.id || 0);
      if (response.status === 200 || response.status === 201) {
        setNotificationType(newNotificationType);
      } else {
        setNotificationType(prevNotificationType);
        // console.log('error');
      }
    } catch (error: any) {
      setNotificationType(prevNotificationType);
      return;
    }
  };

  const handleCloseAndReset = () => {
    handleClose(); // 모달을 닫기 위해 부모로부터 받은 handleClose 호출
  };

  useEffect(() => {
    const handleStorageChange = () => {
      setLanguage(localStorage.getItem('language') || 'ko');
    };
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  return (
    <Dialog
      open={isOpen}
      onClose={handleCloseAndReset}
      sx={{
        width: { xs: '90%', sm: '700px' }, // 모바일에서는 90% 너비
        margin: 'auto'
      }}
    >
      <DialogTitle className="headerTitle" sx={{ bgcolor: theme.palette.table.header, border: 'none' }}>
        <span> {t('employee_notification.ulvac_employee')}</span>
        {/* 엑스 버튼 오른쪽에 배치 */}
        <IconButton onClick={handleCloseAndReset} sx={{ marginLeft: 'auto' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ width: '360px', bgcolor: theme.palette.contentBlock.background, paddingY: '20px !important' }}>
        <Box display="flex" flexDirection="column" gap={1}>
          <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
            <Typography width={80} variant="caption" color={theme.palette.fieldText.title}>
              {t('employee_notification.chart.id')}
            </Typography>
            <Typography variant="h6" fontWeight="bold" color="primary.dark">
              {employeeData?.username}
            </Typography>
          </Box>

          <Box display="flex" flexDirection="row" alignItems="center" gap={1} height={24}>
            <Typography width={80} variant="caption" color={theme.palette.fieldText.title}>
              {t('employee_notification.chart.name')}
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {employeeData?.name}
            </Typography>
          </Box>

          <Box display="flex" flexDirection="row" alignItems="center" gap={1} height={24}>
            <Typography width={80} variant="caption" color={theme.palette.fieldText.title}>
              {t('employee_notification.chart.email')}
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {employeeData?.email}
            </Typography>
          </Box>

          <Box display="flex" flexDirection="row" alignItems="center" gap={1} height={24}>
            <Typography width={80} variant="caption" color={theme.palette.fieldText.title}>
              {t('employee_notification.chart.notification')}
            </Typography>
            <RadioGroup row name="notificationType" value={notificationType} onChange={handleNotificationTypeChange}>
              <FormControlLabel value="EMAIL" control={<Radio size="small" />} label="이메일" sx={{ '& .MuiFormControlLabel-label': { fontSize: '14px' } }} />
              <FormControlLabel value="PHONE" control={<Radio size="small" />} label="휴대전화" sx={{ '& .MuiFormControlLabel-label': { fontSize: '14px' } }} />
            </RadioGroup>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
